/* eslint-disable no-param-reassign */
import { Marker } from '@tomtom-international/web-sdk-maps/dist/maps.min';
import {
    trackSessionNamesForPolygon,
    setMarkerElementLoadingContent,
    setMarkerElementSessionNamesContent,
} from '../helpers';

/**
 * Mutation names
 */
const mutationNames = {
    SET_MODE: 'setDrawingMode',
    SET_FINISHED: 'setFinished',
    SET_LNGLATS: 'setDrawingLngLats',
    SET_POINTS: 'setDrawingPoints',
    SET_MAP_MARKER: 'setMapMarker',
};

/**
 * Module state
 */
const state = {
    isDrawingModeActive: false,
    isDrawingFinished: false,
    lngLats: [],
    points: [],
    mapMarker: null,
};

/**
 * Module getters
 */
const getters = {};

/**
 * Module mutations. Their names are defined as strings-values from map
 * `mutationNames`.
 */
const mutations = {
    /**
     * Sets flag for polygon drawing mode
     * @param {Boolean} active
     */
    [mutationNames.SET_MODE]: (state, active) => {
        state.isDrawingModeActive = active;
    },

    /**
     * Determines whether the polugondrawing is finished
     * @param {Boolean} finished
     */
    [mutationNames.SET_FINISHED]: (state, finished) => {
        state.isDrawingFinished = finished;
    },

    /**
     * Set lists on lng and lat point values of the polygon drawing
     * @param {Object[]} lngLats
     */
    [mutationNames.SET_LNGLATS]: (state, lngLats) => {
        state.lngLats = lngLats;
    },

    /**
     * Set lists on x and y point values of the polygon drawing
     * @param {Object[]} points
     */
    [mutationNames.SET_POINTS]: (state, points) => {
        state.points = points;
    },

    /**
     * Sets current track names displayed beside map marker
     * @param {Object} marker - Instance of TT Marker.
     */
    [mutationNames.SET_MAP_MARKER]: (state, marker) => {
        state.mapMarker = marker;
    },
};

/**
 * Module actions
 */
const actions = {

    /**
     * Starts polygon drawing mode.
     */
    startDrawingMode({ commit }) {
        commit(mutationNames.SET_MODE, true);
    },

    /**
     * Ends polygon drawing mode.
     */
    endDrawingMode({ commit }) {
        commit(mutationNames.SET_MODE, false);
        commit(mutationNames.SET_FINISHED, false);
        commit(mutationNames.SET_POINTS, []);
        commit(mutationNames.SET_LNGLATS, []);
    },

    setFinished({ commit }) {
        commit(mutationNames.SET_FINISHED, true);
    },

    /**
     * Adds a point to the polygon drawing.
     */
    addDrawingPoint(
        { state: { isDrawingFinished, lngLats, points }, commit },
        {
            lat, lng, x, y,
        },
    ) {
        if (isDrawingFinished) return;
        const newPoints = [...points, { x, y }];
        commit(mutationNames.SET_POINTS, newPoints);
        const newLngLats = [...lngLats, { lng, lat }];
        commit(mutationNames.SET_LNGLATS, newLngLats);
    },

    /**
     * Sets all points xy coordinates after map was moved.
     */
    setDrawingPoints(
        { state: { isDrawingFinished }, commit },
        newPoints,
    ) {
        commit(mutationNames.SET_POINTS, newPoints);
    },

    selectCoordinates: (
        {
            commit,
            dispatch,
            state: { mapMarker },
        },
        { lat, lng, map },
    ) => {
        if (mapMarker) {
            mapMarker.setLngLat([lng, lat]);

            setMarkerElementLoadingContent(mapMarker.getElement());
        } else {
            const markerElement = document.createElement('div');
            markerElement.classList.add('track-map-marker');

            setMarkerElementLoadingContent(markerElement);

            const marker = new Marker({
                element: markerElement,
            })
                .setLngLat([lng, lat])
                .addTo(map);

            commit(
                mutationNames.SET_MAP_MARKER,
                marker,
            );
        }

        dispatch('fetchSessionsInArea');
    },

    async fetchSessionsInArea(
        {
            state: { lngLats, mapMarker },
            dispatch,
            rootGetters: { momaURL },
            rootState: {
                map: { internalStartDate: startDate, internalEndDate: endDate },
            },
        },
    ) {
        const closedLngLats = [...lngLats, lngLats[0]];
        const wktPoints = closedLngLats.map(({ lat, lng }) => `${lng} ${lat}`).join(', ');
        const wkt = `POLYGON ((${wktPoints}))`;
        const sessionNames = await trackSessionNamesForPolygon({
            urlBuilder: momaURL, wkt, startDate, endDate,
        });

        let disabledTimeout;

        const onCloseClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            dispatch('clearMarker');

            if (disabledTimeout) {
                clearTimeout(disabledTimeout);
            }
        };

        const onCopyClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (event.target.hasAttribute('disabled')) {
                return;
            }

            const joinedSessionNames = sessionNames.join('\n');

            if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
                window.navigator.clipboard.writeText(joinedSessionNames);
            }

            if (window.clipboardData && window.clipboardData.setData) {
                // legacy IE clipboard support
                window.clipboardData.setData('Text', joinedSessionNames);
            }

            event.target.setAttribute('disabled', 'disabled');

            const previousText = event.target.innerHTML;

            event.target.innerHTML = 'COPIED';

            disabledTimeout = setTimeout(() => {
                if (event.target) {
                    event.target.removeAttribute('disabled');

                    event.target.innerHTML = previousText;
                }
            }, 3000);
        };

        setMarkerElementSessionNamesContent({
            element: mapMarker.getElement(),
            sessionNames,
            onCloseClick,
            onCopyClick,
            disabledTimeout,
            isPolygon: true,
        });
    },

    clearMarker({ state: { mapMarker } }) {
        if (mapMarker) {
            mapMarker.getElement().innerHTML = '';
        }
    },
};

export default {
    namespaced: true,
    actions,
    getters,
    mutationNames,
    mutations,
    state,
};
